/**
 * MANAGEMENT (14) / ONBOARDING-REQUESTS — the LAB-ACCESS-REQUEST review contract. The operator side of the
 * public self-signup intake funnel: a prospect submits a `LabAccessRequest` (statut `demande`), an operator
 * triages it (→ `en_revue`), then approves (→ provision a Laboratoire + owner, `approuvee`) or rejects it
 * (`rejetee`, with a required reason). ADDITIVE to the shell `../types.ts` + the sibling
 * `./onboarding.types.ts` (the lab-directory/provision contract) — it re-uses the provision payload blocks
 * for the approve path rather than re-declaring them.
 *
 * Verified field-for-field against
 *   server/src/modules/management/onboardingRequests.service.js  (toRequestDto / listRequests / the transitions)
 *   server/src/modules/management/onboardingRequests.validators.js (list/get/review/approve/reject schemas)
 * All BigInt ids arrive as strings (the 0056 serialization guard stringifies them server-side). Wire query
 * keys are the server's snake_case; the hooks map camelCase filter state onto them.
 */
import type { ProvisionAbonnementBlock, ProvisionLabBlock, ProvisionLabResult, ProvisionOwnerBlock } from "./onboarding.types";
import type { Page } from "../types";

/** Mirrors the Prisma `StatutDemandeAcces` enum — the intake funnel lifecycle. */
export type DemandeStatut = "demande" | "en_revue" | "approuvee" | "rejetee";

/** The four lifecycle statuts, in funnel order (the inbox filter + the workflow strip iterate this). */
export const DEMANDE_STATUTS: ReadonlyArray<DemandeStatut> = [
  "demande",
  "en_revue",
  "approuvee",
  "rejetee",
];

/**
 * One lab-access-request row (onboardingRequests.service → toRequestDto). NO captcha proof is surfaced.
 * `message` folds in the prospect's contact name + commune + free text (the intake has no first-class
 * nom_contact column — see submitPublicRequest).
 */
export interface LabAccessRequestRow {
  id: string;
  reference: string;
  nomLabo: string;
  wilaya: string;
  email: string;
  telephone: string | null;
  message: string | null;
  statut: DemandeStatut;
  reviewedBy: string | null;
  reviewedAt: string | null;
  raisonRejet: string | null;
  laboratoireId: string | null;
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Inbox filters (all optional; an empty object pages the whole funnel). snake_case = the exact wire keys. */
export interface LabAccessRequestFilters {
  statut?: DemandeStatut;
  wilaya?: string;
  q?: string;
  include_archived?: boolean;
  page?: number;
  page_size?: number;
}

/** GET /management/onboarding/requests response — the shared page envelope over request rows. */
export type LabAccessRequestPage = Page<LabAccessRequestRow>;

// ── Transition bodies (correct-by-reissue, optimistic-version-guarded) ─────────────
/** POST …/:id/review — take the request into triage (demande → en_revue). */
export interface ReviewRequestVars {
  id: string;
  note?: string;
  expected_version?: number;
}

/**
 * POST …/:id/approve — provision a Laboratoire + first owner, then link + close. Re-uses the provision
 * payload blocks (the operator supplies the governance fields the public intake could not).
 */
export interface ApproveRequestVars {
  id: string;
  laboratoire: ProvisionLabBlock;
  owner: ProvisionOwnerBlock;
  abonnement?: ProvisionAbonnementBlock;
  expected_version?: number;
}

/** POST …/:id/approve response — the closed request + the provision result (onboardingRequests.service). */
export interface ApproveRequestResult {
  request: LabAccessRequestRow;
  provision: ProvisionLabResult;
}

/** POST …/:id/reject — record the REQUIRED raison_rejet + close (demande|en_revue → rejetee). */
export interface RejectRequestVars {
  id: string;
  raison_rejet: string;
  expected_version?: number;
}
